import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { ReplaySubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { authCodeFlowConfig, Claims } from '../auth.config';
import {
  FetchUserDetailsIfEmpty,
  RemoveUserName
} from '../states/user-state/user.actions';
import { AuthErrorMessages } from 'src/app/shared/authErrorMessages';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  
  public constructor(
    private oauthService: OAuthService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  public configure(): void {
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.events.subscribe(event => {
      if(event instanceof OAuthErrorEvent){
        const errorMessage = AuthErrorMessages.getErrorMessage(event.type);

        if(errorMessage !== AuthErrorMessages.defaultErrorMessage)
          alert(errorMessage);
        else
          console.warn(errorMessage);
      }
    });
    this.loadUserProfileAfterLogin();
    this.loadUserNameOnRefresh();
    this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then(() => this.isDoneLoadingSubject$.next(true))
      .catch(() => this.isDoneLoadingSubject$.next(true));

    // eslint-disable-next-line quote-props
    this.oauthService.setupAutomaticSilentRefresh(
      // eslint-disable-next-line @typescript-eslint/naming-convention
      { login_hint: this.getName() },
      'access_token'
    );
  }

  public login(): void {
    this.oauthService.initLoginFlow();
  }

  public logout(): void {
    this.store.dispatch(new RemoveUserName());
    this.oauthService.logOut();
  }

  public isAuthorized(): boolean {
    const hasValidAccessToken = this.oauthService.hasValidAccessToken();

    return hasValidAccessToken;
  }

  private loadUserProfileAfterLogin(): void {
    this.oauthService.events
      .pipe(filter((e) => e.type === 'token_received'))
      .subscribe(() => {
        this.oauthService.loadUserProfile().then(() => {
          this.store.dispatch(new FetchUserDetailsIfEmpty());
          // Clear query string parameters after token is received
          this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        });
      });
  }

  private loadUserNameOnRefresh(): void {
    this.oauthService.events
      .pipe(filter((e) => e.type === 'discovery_document_loaded'))
      .subscribe(() => {
        if (this.isAuthorized()) {
          this.store.dispatch(new FetchUserDetailsIfEmpty());
        }
      });
  }

  private getName(): string {
    const claims = this.oauthService.getIdentityClaims() as Claims;
    if (!claims) {
      return '';
    }
    return `${claims.name}`;
  }
}
